import { merge } from 'lodash-es';
import { fetchAndUpdateSessionExpiration } from 'platform/utilities/api';
import environment from 'platform/utilities/environment';
import localStorage from 'platform/utilities/storage/localStorage';
import manifest from '../manifest.json';
import { CLAIMANT_ID_PATTERN, getSignInUrl, SORT_DEFAULTS } from './constants';
import { paramUpdate } from './helpers';

// Set app name for request headers
window.appName = manifest.entryName;

const API_VERSION = 'accredited_representative_portal/v0';
const doNotRedirectUrl = [
  manifest.rootUrl,
  `${manifest.rootUrl}/`,
  `${manifest.rootUrl}/help`,
  `${manifest.rootUrl}/help/quicksubmit`,
  `${manifest.rootUrl}/sign-in`,
  `${manifest.rootUrl}/auth/login/callback`,
];

// 403 redirect handler
const redirectToUnauthorizedAndReturn = () => {
  const inAppPath = window.location.pathname.startsWith(manifest.rootUrl);
  if (inAppPath) {
    window.location.replace(`${manifest.rootUrl}/dashboard?unauthorized`);
    // Keep loaders pending until navigation completes to avoid UI flash
    return new Promise(() => {});
  }
  return null;
};

/**
 * A request that never got a response, for example offline or on a dropped
 * connection. It extends TypeError, which fetch rejects with in that case, so
 * code that checks for a TypeError still matches it.
 */
export class NetworkError extends TypeError {}

// fetch rejects with a TypeError only when the request never got a response.
// Its message differs by browser and version, so the type alone decides.
const toNetworkError = e => {
  throw e instanceof TypeError ? new NetworkError(e.message) : e;
};

/**
 * Enhanced API wrapper that preserves Response objects for error handling
 * while maintaining existing platform functionality where beneficial
 */
const wrapApiRequest = fn => {
  return async (...args) => {
    // Set up request options similarly to platform apiRequest
    const optionsFromCaller = args[fn.length] || {};
    const [resource, optionsFromFn = {}] = fn(...args.slice(0, fn.length));

    const skip403Redirect =
      optionsFromCaller.skip403Redirect || optionsFromFn.skip403Redirect;

    const csrfTokenStored = localStorage.getItem('csrfToken');

    const defaultSettings = {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Key-Inflection': 'camel',
        'Source-App-Name': window.appName,
        'X-CSRF-Token': csrfTokenStored,
        'Content-Type': 'application/json',
      },
    };

    const settings = merge(defaultSettings, optionsFromFn, optionsFromCaller);

    // Build URL like platform apiRequest
    const baseUrl = `${environment.API_URL}/${API_VERSION}`;
    const url = resource[0] === '/' ? [baseUrl, resource].join('') : resource;

    try {
      // Reuse session management from platform
      const response = await fetchAndUpdateSessionExpiration(
        url,
        settings,
      ).catch(toNetworkError);

      // Handle CSRF token updates
      const csrfToken = response.headers.get('X-CSRF-Token');
      if (csrfToken && csrfToken !== csrfTokenStored) {
        localStorage.setItem('csrfToken', csrfToken);
      }

      // For successful responses, return data
      if (response.ok || response.status === 304) {
        return response;
      }

      // For 401s, redirect to login
      if (
        response.status === 401 &&
        // Don't redirect to login for our app's root / landing page experience.
        // People are allowed to be unauthenticated there.
        // TODO: probably need a more sound & principled solution here.
        !doNotRedirectUrl.includes(window.location.pathname)
      ) {
        window.location = getSignInUrl({
          returnUrl: window.location.href,
        });
        return null;
      }

      // For 403s, allow opt-out of redirect for special cases
      if (response.status === 403) {
        if (skip403Redirect) {
          throw response;
        }
        const redirected = redirectToUnauthorizedAndReturn();
        if (redirected) return redirected;
        throw response;
      }

      // For errors, preserve the Response object
      throw response;
    } catch (err) {
      // Mirror 403 handling when fetch throws a Response
      if (err instanceof Response && err.status === 403) {
        if (skip403Redirect) {
          throw err;
        }
        const redirected = redirectToUnauthorizedAndReturn();
        if (redirected) return redirected;
      }

      // Network-like errors - propagate to caller
      throw err;
    }
  };
};

/**
 * A claimant ID as a path segment. React Router hands a loader the decoded
 * route param, so `..%2Fother` in the address bar arrives as `../other` and
 * would climb out of `/claimant/`. The loaders check the pattern before any
 * request. This is the last check, so no request path carries an id that
 * fails it.
 *
 * @param {string} id The claimant's portal ID.
 * @returns {string} The id, encoded for a path.
 * @throws {RangeError} For an id that is not a string of the allowed
 *   characters. The message holds no part of the id.
 */
const claimantPath = id => {
  if (typeof id !== 'string' || !CLAIMANT_ID_PATTERN.test(id)) {
    throw new RangeError('The claimant ID is not valid');
  }
  return encodeURIComponent(id);
};

const paginationDefaults = `page[size]=${SORT_DEFAULTS.SIZE}&page[number]=${SORT_DEFAULTS.NUMBER}`;
const sortDefaults = `&sort[by]=${SORT_DEFAULTS.SORT_BY}&sort[order]=${SORT_DEFAULTS.SORT_ORDER}`;

const api = {
  // Lightweight authorization check used by Dashboard loader
  checkAuthorized: wrapApiRequest(() => {
    return ['/authorize_as_representative'];
  }),

  getPOARequests: wrapApiRequest(query => {
    const status = query.status ? `status=${query.status}` : '';
    const pagination = query.size
      ? `&page[size]=${query.size}&page[number]=${query.number}`
      : paginationDefaults;

    const sortParam = paramUpdate(query.sort, query.status);
    const sort = sortParam
      ? `&sort[by]=${sortParam.sortBy}&sort[order]=${sortParam.order}`
      : sortDefaults;

    const showAllRequests = query.selectedIndividual === 'you';
    const selectedIndividual = query.selectedIndividual
      ? `&as_selected_individual=${showAllRequests}`
      : `&as_selected_individual=${SORT_DEFAULTS.SELECTED_INDIVIDUAL}`;

    const params = `${status + pagination + sort + selectedIndividual}`;
    return [`/power_of_attorney_requests${params ? '?' : ''}${params}`];
  }),

  getSubmissions: wrapApiRequest(query => {
    const pagination = query.size
      ? `&page[size]=${query.size}&page[number]=${query.number}`
      : paginationDefaults;

    const subParam = paramUpdate(query.sort);
    const sort = subParam
      ? `&sort[by]=${subParam.sortBy}&sort[order]=${subParam.order}`
      : sortDefaults;

    return [`/claim_submissions?${pagination}${sort}`];
  }),

  getClaimantSubmissions: wrapApiRequest(query => {
    const pagination = query.size
      ? `&page[size]=${query.size}&page[number]=${query.number}`
      : paginationDefaults;

    const subParam = paramUpdate(query.sort);
    const sort = subParam
      ? `&sort[by]=${subParam.sortBy}&sort[order]=${subParam.order}`
      : sortDefaults;

    return [
      `/claimant_claim_submissions/${claimantPath(
        query.identifier,
      )}?${pagination}${sort}`,
    ];
  }),

  claimantSearch: wrapApiRequest(data => {
    return [
      `/claimant/search`,
      {
        body: JSON.stringify({ ...data }),
        method: 'POST',
      },
    ];
  }),

  getPOARequest: wrapApiRequest(id => {
    return [`/power_of_attorney_requests/${id}`];
  }),

  getUser: wrapApiRequest(() => {
    return ['/user'];
  }),

  createPOARequestDecision: wrapApiRequest((id, decision) => {
    return [
      `/power_of_attorney_requests/${id}/decision`,
      {
        body: JSON.stringify({ decision }),
        method: 'POST',
      },
    ];
  }),

  getClaimantOverview: wrapApiRequest(id => {
    return [`/claimant/${claimantPath(id)}`, { skip403Redirect: true }];
  }),

  // The claimant's MPI record with the full SSN, which the overview route
  // leaves out. With no isVeteran param, vets-api asks VA Profile, and answers
  // under veteranSubPage only for a Veteran.
  getClaimantPrefill: wrapApiRequest(id => {
    return [`/claimant/prefill/${claimantPath(id)}`, { skip403Redirect: true }];
  }),

  // Mints a one-time QuickSubmit embed session. The route is not under ARP's
  // own prefix, so the URL is absolute. A 403 shows as an alert in the embed's
  // place, not a jump to the dashboard. vets-api reads the rep's accreditation
  // and organizations from their own record, so the body is an empty object.
  createQuickSubmitSession: wrapApiRequest(() => {
    return [
      `${environment.API_URL}/quick_submit/v0/arp/session`,
      {
        method: 'POST',
        body: '{}',
        skip403Redirect: true,
      },
    ];
  }),

  // The combined history lives under the QuickSubmit module, so the resource
  // is a full URL rather than a path under API_VERSION. With a `claimantId`
  // vets-api answers that one claimant's history, which is every form for
  // them under a power of attorney the representative holds, whoever filed
  // it, plus their uploads. Without one it answers the representative's own
  // history. The query is the only parameter, so `wrapApiRequest` reads the
  // caller's options from the second argument. A 403 redirects to the
  // dashboard, as on the other ARP pages, unless the caller passes
  // `skip403Redirect`. The claimant history loader does, and says why.
  getCombinedHistory: wrapApiRequest(query => {
    const claimantId = query?.claimantId;
    const search = claimantId
      ? `?claimant_id=${encodeURIComponent(claimantId)}`
      : '';
    return [
      `${environment.API_URL}/quick_submit/v0/arp/submission_history${search}`,
    ];
  }),

  // remove eslint disable when query is implemented

  getInProgressForms: wrapApiRequest(query => {
    const size = query.get('size');
    const number = query.get('perPage');
    const paginationQuery = size
      ? `&page[size]=${size}&page[number]=${number}`
      : paginationDefaults;

    const sort = query.get('sort');
    const sortQuery = sort
      ? `&sort[by]=updated_at&sort[order]=${sort === 'newest' ? 'desc' : 'asc'}`
      : sortDefaults;

    return [
      `/representative_in_progress_forms/?${paginationQuery}${sortQuery}`,
    ];
  }),

  // Same endpoint as checkAuthorized, but returns the 403 response instead of
  // redirecting away, so the caller can read `pivClaimEligible` off its body.
  checkPivClaimEligibility: wrapApiRequest(() => {
    return ['/authorize_as_representative', { skip403Redirect: true }];
  }),

  requestPivClaimCode: wrapApiRequest((registrationNumber, individualType) => {
    return [
      '/piv_claim/request_code',
      {
        method: 'POST',
        skip403Redirect: true,
        // Sent camelCase; ARP bypasses OliveBranch's request underscoring, so the controller reads
        // these keys as-is.
        body: JSON.stringify({ registrationNumber, individualType }),
      },
    ];
  }),

  verifyPivClaimCode: wrapApiRequest(code => {
    return [
      '/piv_claim/verify_code',
      {
        method: 'POST',
        skip403Redirect: true,
        body: JSON.stringify({ code }),
      },
    ];
  }),
};

export default api;
